import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Alert,
  LinearProgress
} from '@mui/material';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import ThermostatOutlinedIcon from '@mui/icons-material/ThermostatOutlined';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import BuildIcon from '@mui/icons-material/Build';
import PowerIcon from '@mui/icons-material/Power';
import SpeedIcon from '@mui/icons-material/Speed';
import * as d3 from 'd3';
import { getUnitByName, generateFreezerMockData } from '../../data/freezerMockData';

const FreezerEntityDrawer = ({ entityDetails, chartRef }) => {
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [allUnitsData, setAllUnitsData] = useState(null);

  useEffect(() => {
    // Generate all units data
    const unitsData = generateFreezerMockData();
    setAllUnitsData(unitsData);

    let foundUnit = null;

    // Find the specific unit based on entityDetails name
    if (entityDetails && entityDetails.name) {
      foundUnit = unitsData.units.find(unit => unit.name === entityDetails.name);
      
      // If not found by exact name, try MAC address from keys as fallback
      if (!foundUnit && entityDetails.keys) {
        const macAddressKey = entityDetails.keys.find(key => key.keyName === 'mac_address');
        if (macAddressKey) {
          foundUnit = getUnitByName(macAddressKey.value);
        }
      }
    }
    
    // Fallback to first unit if no specific unit found
    if (!foundUnit && unitsData.units.length > 0) {
      foundUnit = unitsData.units[0];
    }

    setSelectedUnit(foundUnit);
  }, [entityDetails]);

  const renderTemperatureChart = useCallback(() => {
    if (!selectedUnit || !chartRef.current) return;
    d3.select(chartRef.current).selectAll('*').remove();

    const width = 400;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 60, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const data = selectedUnit.temp7d;

    const xScale = d3.scaleBand()
      .domain(data.map(d => d.day))
      .range([0, chartWidth])
      .padding(0.1);

    // Temperature scale (negative values). Add some padding around range
    const minTemp = d3.min(data, d => d.avg);
    const maxTemp = d3.max(data, d => d.avg);
    const yScale = d3.scaleLinear()
      .domain([Math.min(minTemp, selectedUnit.targetRangeC.min) - 1, Math.max(maxTemp, selectedUnit.targetRangeC.max) + 1])
      .range([chartHeight, 0]);

    // Axes
    svg.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '10px');

    svg.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .text('Temperature (°C)')
      .style('font-size', '12px');

    // Target range band
    svg.append('rect')
      .attr('x', 0)
      .attr('y', yScale(selectedUnit.targetRangeC.max))
      .attr('width', chartWidth)
      .attr('height', Math.max(1, yScale(selectedUnit.targetRangeC.min) - yScale(selectedUnit.targetRangeC.max)))
      .attr('fill', '#E3F2FD');

    // Line
    const line = d3.line()
      .x(d => xScale(d.day) + xScale.bandwidth() / 2)
      .y(d => yScale(d.avg));

    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#1976D2')
      .attr('stroke-width', 3)
      .attr('d', line);

    // Dots
    svg.selectAll('.temp-dot')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'temp-dot')
      .attr('cx', d => xScale(d.day) + xScale.bandwidth() / 2)
      .attr('cy', d => yScale(d.avg))
      .attr('r', 4)
      .attr('fill', '#1976D2');

    // Title
    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Temperature Trends - Last 7 Days');
  }, [selectedUnit]);

  useEffect(() => {
    if (chartRef.current && selectedUnit && selectedUnit.temp7d.length > 0) {
      renderTemperatureChart();
    }
  }, [selectedUnit, renderTemperatureChart]);

  // Show loading state if no unit data
  if (!selectedUnit || !allUnitsData) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading freezer/cooler data...
        </Typography>
      </Box>
    );
  }

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Critical': return 'error';
      case 'Warning': return 'warning';
      case 'OK': return 'success';
      default: return 'primary';
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'Excellent': return 'success';
      case 'Good': return 'primary';
      case 'Fair': return 'warning';
      case 'Poor': return 'error';
      default: return 'primary';
    }
  };

  // Get temperature status color based on range
  const getTempColor = (temp, targetRange) => {
    if (temp < targetRange.min - 2 || temp > targetRange.max + 2) return 'error.main';
    if (temp < targetRange.min || temp > targetRange.max) return 'warning.main';
    return 'success.main';
  };

  return (
    <>
      {/* Freezer/Cooler Unit Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center">
              <AcUnitOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">
                  {selectedUnit.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedUnit.location}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Chip 
                label={selectedUnit.status} 
                color={getStatusColor(selectedUnit.status)}
                variant="outlined" 
              />
              <Chip 
                label={selectedUnit.health} 
                color={getHealthColor(selectedUnit.health)}
                variant="outlined" 
              />
            </Box>
          </Box>

          {/* Status Alert */}
          {(selectedUnit.status === 'Critical' || selectedUnit.status === 'Warning') && (
            <Alert severity={selectedUnit.status === 'Critical' ? 'error' : 'warning'} sx={{ mb: 2 }}>
              {selectedUnit.status === 'Critical' && 'Unit is in critical condition and requires immediate attention.'}
              {selectedUnit.status === 'Warning' && 'Unit is experiencing issues and should be checked soon.'}
            </Alert>
          )}
          
          {/* Temperature Metrics Summary */}
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Temperature & Performance Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography 
                    variant="h6" 
                    color={getTempColor(selectedUnit.currentTempC, selectedUnit.targetRangeC)}
                  >
                    {selectedUnit.currentTempC.toFixed(1)}°C
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Current Temp
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="info.main">
                    {selectedUnit.targetTempC}°C
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Target Temp
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography 
                    variant="h6" 
                    color={selectedUnit.performance.efficiency > 85 ? 'success.main' : 
                           selectedUnit.performance.efficiency > 70 ? 'warning.main' : 'error.main'}
                  >
                    {selectedUnit.performance.efficiency}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Efficiency
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="secondary.main">
                    {selectedUnit.performance.powerConsumption}W
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Power Usage
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Unit Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Unit Information
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <AcUnitOutlinedIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Unit Type" 
                secondary={`${selectedUnit.category} - Target Range: ${selectedUnit.targetRangeC.min}°C to ${selectedUnit.targetRangeC.max}°C`} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AccessTimeIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Last Maintenance" 
                secondary={new Date(selectedUnit.lastMaintenanceAt).toLocaleString()} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PowerIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Performance" 
                secondary={`${selectedUnit.performance.compressorRuntime}% Runtime, ${selectedUnit.performance.fanSpeed}% Fan Speed`} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SpeedIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Usage" 
                secondary={`${selectedUnit.performance.dailyOpenings} openings/day, ${selectedUnit.performance.avgOpenDuration}s avg duration`} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            System Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {allUnitsData.summary.totalUnits}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total Units
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="error.main">
                  {allUnitsData.summary.criticalUnits}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Critical
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="warning.main">
                  {allUnitsData.summary.warningUnits}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Warning
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="success.main">
                  {allUnitsData.summary.okUnits}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  OK
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Temperature Trends Chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <ThermostatOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Temperature Trends
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            7-day temperature monitoring showing daily averages and target range compliance.
          </Typography>
          <Box
            ref={chartRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '320px'
            }}
          />
        </CardContent>
      </Card>

      {/* Sensor Status */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Sensor Status
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <ThermostatOutlinedIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Temperature Sensor" 
                secondary={selectedUnit.sensors.temperatureSensor} 
              />
              <Chip 
                label={selectedUnit.sensors.temperatureSensor} 
                color={selectedUnit.sensors.temperatureSensor === 'Online' ? 'success' : 'error'}
                size="small"
                variant="outlined"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <BatteryFullIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Battery Level" 
                secondary={`${selectedUnit.sensors.batteryLevel}%`} 
              />
              <Chip 
                label={`${selectedUnit.sensors.batteryLevel}%`} 
                color={selectedUnit.sensors.batteryLevel > 20 ? 'success' : 'warning'}
                size="small"
                variant="outlined"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SignalWifi4BarIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Signal Strength" 
                secondary={`${selectedUnit.sensors.signalStrength}%`} 
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Alerts and Maintenance */}
      {(selectedUnit.alerts.temperatureAlerts.length > 0 || 
        selectedUnit.alerts.maintenanceAlerts.length > 0 ||
        selectedUnit.alerts.sensorAlerts.length > 0) && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">
                Alerts & Maintenance
              </Typography>
            </Box>
            <List dense>
              {selectedUnit.alerts.temperatureAlerts.map((alert, index) => (
                <ListItem key={`temp-${index}`}>
                  <ListItemIcon>
                    <ThermostatOutlinedIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Temperature Alert"
                    secondary={alert}
                  />
                </ListItem>
              ))}
              {selectedUnit.alerts.maintenanceAlerts.map((alert, index) => (
                <ListItem key={`maint-${index}`}>
                  <ListItemIcon>
                    <BuildIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Maintenance Alert"
                    secondary={alert}
                  />
                </ListItem>
              ))}
              {selectedUnit.alerts.sensorAlerts.map((alert, index) => (
                <ListItem key={`sensor-${index}`}>
                  <ListItemIcon>
                    <WarningIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sensor Alert"
                    secondary={alert}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default FreezerEntityDrawer;


